import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  HttpLink,
} from "@apollo/client";
import { onError as mkApolloOnError } from "@apollo/client/link/error";
import { createAuthLink } from "aws-appsync-auth-link";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import "./App.css";
import { SurveyPage } from "./survey/SurveyContainer";
import { HomePage } from "./HomePage";
import * as Sentry from "@sentry/browser";

// <reference path="stubs.env.d.ts"/>
import * as env from "env";

const debugBorders = false;
export const DebugBorders = React.createContext(debugBorders);

const theme = createTheme({
  palette: {
    primary: {
      // Mainstay primary green
      main: "#20AD96",
      // for example, this controls the text color in a primary button
      contrastText: "#fff",
    },
    background: {
      default: "#F9FAFB",
    },
  },
});

const onApolloError = mkApolloOnError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      const errTxt = `GraphQL error: message=${message}, location=${JSON.stringify(
        locations
      )}, path=${path}`;
      console.error(errTxt);
      Sentry.captureException(errTxt);
    });

    if (networkError) {
      const errTxt = `GraphQL network error: ${networkError}`;
      console.error(errTxt);
      Sentry.captureException(errTxt);
    }
  }
});

const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    onApolloError,
    createAuthLink({
      url: env.GQL_AWS_APPSYNC_API_URL,
      region: env.GQL_AWS_APPSYNC_REGION,
      auth: {
        type: "API_KEY",
        apiKey: env.GQL_AWS_APPSYNC_API_KEY,
        // jwtToken: async () => token, // Required when you use Cognito UserPools OR OpenID Connect. token object is obtained previously
        // credentials: async () => credentials, // Required when you use IAM-based auth.
      },
    }),
    new HttpLink({
      uri: env.GQL_AWS_APPSYNC_API_URL,
    }),
  ]),
  cache: new InMemoryCache(),
});

const injectHeapAnalytics = (): void => {
  const innerHTML = `
    window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
    heap.load("${env.HEAP_APP_ID}");
  `;

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.innerHTML = innerHTML;
  script.async = true;

  document.body.appendChild(script);
};

function App() {
  useEffect(() => {
    env?.HEAP_APP_ID !== "" && injectHeapAnalytics();
  }, []);

  return (
    <>
      <DebugBorders.Provider value={debugBorders}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ApolloProvider client={apolloClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Routes>
                <Route path="/:id" element={<SurveyPage />} />
                <Route path="/" element={<HomePage />} />
              </Routes>
            </LocalizationProvider>
          </ApolloProvider>
        </ThemeProvider>
      </DebugBorders.Provider>
    </>
  );
}

export default App;
